// ============================================================================
// LAYOUT COMPONENT
// Main app layout with sidebar navigation
// Based on: _DESIGN_REFERENCE/client/src/components/layout.tsx
// ============================================================================

import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
    Activity,
    CalendarDays,
    BookOpen,
    LayoutDashboard,
    Settings,
    Menu,
    X,
    Zap,
    Trophy,
} from 'lucide-react'
import { FEATURE_LOGBOOK, FEATURE_PROGRESS_PAGE } from '@/types'
import { useStore } from '@/lib/store'
import { useState, useEffect } from 'react'
import { ReadinessWidget } from '@/components/ReadinessWidget'
import { ProfileSwitcher } from '@/components/features/profile/ProfileSwitcher'
import { AddAthleteDialog } from '@/components/features/profile/AddAthleteDialog'
import { InteractionHub } from '@/components/features/discovery/InteractionHub'
import { GhostOverlay } from '@/components/features/discovery/GhostOverlay'
import { RewardSimulation } from '@/components/features/discovery/RewardSimulation'
import { Breadcrumbs } from '@/components/layout/Breadcrumbs'
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner'
import { Badge } from '@/components/ui/badge'

interface LayoutProps {
    children?: React.ReactNode
}

interface NavItem {
    href: string;
    label: string;
    icon: React.ElementType;
    mobileOnly?: boolean;
    desktopOnly?: boolean;
}

interface SidebarContentProps {
    filteredNavItems: NavItem[];
    location: ReturnType<typeof useLocation>; // or Location from 'react-router-dom' but ReturnType is safer fast fix
    setIsMobileOpen: (open: boolean) => void;
    navigate: ReturnType<typeof useNavigate>; // or NavigateFunction
    setIsAddAthleteOpen: (open: boolean) => void;
}

const SidebarContent = ({ filteredNavItems, location, setIsMobileOpen, navigate, setIsAddAthleteOpen }: SidebarContentProps) => {
    const preferences = useStore(state => state.preferences);
    const subscription = useStore(state => state.subscription);
    return (
        <div className="flex flex-col h-full bg-sidebar text-sidebar-foreground p-4">
            {/* Logo */}
            <Link to="/" onClick={() => setIsMobileOpen(false)} className="flex items-center gap-3 px-2 py-6 mb-4 hover:opacity-80 transition-opacity">
                <div className="relative">
                    <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-primary-foreground font-bold text-lg">
                        <Zap className="w-5 h-5" />
                    </div>
                    {preferences.showEducation && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-indigo-500 rounded-full border-2 border-sidebar ring-4 ring-indigo-500/20 animate-pulse" title="Methodology Feedback Active" />
                    )}
                </div>
                <span className="font-semibold text-lg tracking-tight">Adaptive Endurance</span>
                {subscription.isPro && (
                    <Badge variant="secondary" className="bg-gradient-to-r from-amber-500 to-orange-600 text-white border-none shadow-sm animate-pulse ml-0.5 px-1.5 py-0 text-[10px]">
                        PRO
                    </Badge>
                )}
            </Link>

            {/* Navigation */}
            <nav className="flex-1 space-y-1">
                {filteredNavItems.map((item) => {
                    const isActive = location.pathname === item.href || (item.href !== '/' && location.pathname.startsWith(item.href));
                    return (
                        <Link
                            key={item.href}
                            to={item.href}
                            data-tour={`nav-item-${item.href}`}
                            onClick={() => setIsMobileOpen(false)}
                            className={cn(
                                'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200',
                                isActive
                                    ? 'bg-white text-primary shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground hover:bg-black/5'
                            )}
                        >
                            <item.icon
                                className={cn(
                                    'w-5 h-5',
                                    isActive ? 'text-primary' : 'text-muted-foreground'
                                )}
                            />
                            {item.label}
                        </Link>
                    )
                })}
            </nav>

            {/* User Profile Switcher */}
            <div className="mt-auto pt-6 border-t border-sidebar-border">
                <div className="mb-4">
                    <ProfileSwitcher
                        onAddAthlete={() => setIsAddAthleteOpen(true)}
                        onManageProfiles={() => navigate('/settings?tab=admin')}
                    />
                </div>

                {/* Readiness Widget */}
                <ReadinessWidget />
            </div>
        </div>
    );
};

export default function Layout({ children }: LayoutProps) {
    const location = useLocation()
    const navigate = useNavigate()
    const [isMobileOpen, setIsMobileOpen] = useState(false)
    const [isAddAthleteOpen, setIsAddAthleteOpen] = useState(false)
    const subscription = useStore(state => state.subscription)

    // Detect mobile for conditional rendering
    const [isMobile, setIsMobile] = useState(false)
    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth < 768)
        checkMobile()
        window.addEventListener('resize', checkMobile)

        // Listen for global add athlete event
        const handleOpenAddAthlete = () => setIsAddAthleteOpen(true);
        window.addEventListener('open-add-athlete', handleOpenAddAthlete);

        return () => {
            window.removeEventListener('resize', checkMobile)
            window.removeEventListener('open-add-athlete', handleOpenAddAthlete);
        }
    }, [])

    const athlete = useStore(state => state.athlete)
    const isCyclingOrTri = athlete.sport === 'Cycling' || athlete.sport === 'Triathlon'

    const checkFeatureAccess = useStore(state => state.checkFeatureAccess);

    const navItems: NavItem[] = [
        { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { href: '/schedule', label: 'Schedule', icon: CalendarDays, desktopOnly: true },
        ...(checkFeatureAccess(FEATURE_LOGBOOK) ? [{ href: '/logbook', label: 'Logbook', icon: BookOpen }] : []),
        { href: '/race', label: 'Race Hub', icon: Trophy },
        ...(checkFeatureAccess(FEATURE_PROGRESS_PAGE) ? [{ href: '/progress', label: 'Progress', icon: Activity }] : []),
        ...(isCyclingOrTri ? [{ href: '/player', label: 'Workout Player', icon: Zap, desktopOnly: true }] : []),
        { href: '/settings', label: 'Settings', icon: Settings },
    ]

    const filteredNavItems = navItems.filter(item => {
        if (item.desktopOnly && isMobile) return false;
        return true;
    });


    const discovery = useStore(state => state.discovery)
    const setRewardType = useStore(state => state.setRewardType)




    // Admin link removed from sidebar per user request (Fix 1). Accessible via Settings only.
    /*
    if (isAdmin && !filteredNavItems.find(i => i.href === '/admin')) {
        filteredNavItems.push({ href: '/admin', label: 'Admin', icon: Shield })
    }
    */

    return (
        <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
            <ImpersonationBanner />
            <div className="flex flex-1">
                {/* Desktop Sidebar */}
                <aside className="hidden md:block w-64 border-r border-sidebar-border fixed inset-y-0 left-0 z-50 mt-[32px] peer-[:empty]:mt-0">
                    <SidebarContent filteredNavItems={filteredNavItems} location={location} setIsMobileOpen={setIsMobileOpen} navigate={navigate} setIsAddAthleteOpen={setIsAddAthleteOpen} />
                </aside>

                {/* Mobile Header */}
                <div className="md:hidden fixed top-0 left-0 right-0 h-16 glass z-50 flex items-center justify-between px-4">
                    <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                        <Zap className="w-6 h-6 text-primary" />
                        <span className="font-semibold text-lg tracking-tight">Adaptive Endurance</span>
                        {subscription.isPro && (
                            <Badge variant="secondary" className="ml-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white border-none px-1.5 py-0 text-[10px]">
                                PRO
                            </Badge>
                        )}
                    </Link>
                    <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsMobileOpen(!isMobileOpen)}
                        data-tour="mobile-menu-button"
                    >
                        {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </Button>
                </div>

                {/* Mobile Slide-out Menu */}
                {isMobileOpen && (
                    <>
                        <div
                            className="md:hidden fixed inset-0 bg-black/50 z-40"
                            onClick={() => setIsMobileOpen(false)}
                        />
                        <div className="md:hidden fixed inset-y-0 left-0 w-72 z-50 bg-sidebar shadow-xl">
                            <SidebarContent filteredNavItems={filteredNavItems} location={location} setIsMobileOpen={setIsMobileOpen} navigate={navigate} setIsAddAthleteOpen={setIsAddAthleteOpen} />
                        </div>
                    </>
                )}

                {/* Main Content */}
                <main className="flex-1 md:ml-64 min-h-screen p-4 md:p-8 pt-20 md:pt-8 pb-24 md:pb-8 overflow-x-hidden">
                    <div className="max-w-[1600px] mx-auto flex flex-col gap-6 w-full">
                        <Breadcrumbs />
                        {children || <Outlet />}
                    </div>
                </main>


                {/* Add Athlete Dialog */}
                <AddAthleteDialog
                    open={isAddAthleteOpen}
                    onOpenChange={setIsAddAthleteOpen}
                />

                {/* Discovery Hub Components */}
                <InteractionHub />
                <GhostOverlay />
                <RewardSimulation
                    type={discovery.rewardType}
                    onComplete={() => setRewardType(null)}
                />
            </div>
        </div >
    )
}
